import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ButtonIcon,
  Icon,
  InputField,
  Typography,
} from '@saviynt/design-system';

import Logger from '../../../../services/loggerService';
import type { RootState } from '../../../../services/serviceTypes';
import {
  resetPromptFooterState,
  setPromptFooterMode,
  setPromptFooterState,
} from '../../data-store/copilotSlice';

import FilePrompt from './FilePrompt/FilePrompt';

import './PromptFooter.css';

export interface PromptFooterProps {
  isDisabled: boolean;
  onFileSubmit: (file: File | null, userInput: string) => void;
}

const MAX_HEIGHT_PX = 116;
const PIXELS_PER_REM = parseFloat(
  getComputedStyle(document.documentElement).fontSize
);

/**
 * PromptFooter component for handling chat input in copilot
 * Fixed footer with input field for user interactions
 */
function PromptFooter({ isDisabled = false, onFileSubmit }: PromptFooterProps) {
  const dispatch = useDispatch();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const { mode, promptFooter } = useSelector((state: RootState) => ({
    mode: state.copilot.ui.promptFooterMode,
    promptFooter: state.copilot.ui.promptFooter,
  }));

  const { value, isLoading, uploadedFile, fileSizeError } = promptFooter;

  const promptInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasTriggeredUpload = useRef(false);
  const promptInputTextSize = value.trim().length;

  // Get fullscreen state
  const isFullscreen = useSelector((state: RootState) => {
    const uiState = state.copilot.ui;

    return uiState.fullscreenMode?.isActive ?? false;
  });

  const characterCount = value.length;
  const maxCharacters = 2000;
  const isCharacterLimitExceeded = characterCount > maxCharacters;

  useEffect(() => {
    if (mode === 'upload') {
      hasTriggeredUpload.current = false;
    }

    if (
      mode === 'upload' &&
      !uploadedFile &&
      !isDisabled &&
      uploadProgress === null &&
      !hasTriggeredUpload.current
    ) {
      hasTriggeredUpload.current = true;
      fileInputRef.current?.click();
    }

    if (mode !== 'upload') {
      hasTriggeredUpload.current = false;
    }
  }, [mode, uploadedFile, isDisabled, uploadProgress]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const maxSize = 5 * 1024 * 1024;

    if (file) {
      if (file.size > maxSize) {
        dispatch(setPromptFooterState({ uploadedFile: file }));
        setUploadProgress(0);

        setTimeout(() => {
          setUploadProgress(50);
        }, 1000);

        setTimeout(() => {
          setUploadProgress(100);
          dispatch(
            setPromptFooterState({
              fileSizeError: 'File size exceeds 5MB limit',
            })
          );
        }, 2000);

        // eslint-disable-next-line no-param-reassign
        event.target.value = '';

        return;
      }

      dispatch(setPromptFooterState({ uploadedFile: file }));
      setUploadProgress(0);

      setTimeout(() => {
        setUploadProgress(50);
      }, 1000);

      setTimeout(() => {
        setUploadProgress(100);
      }, 2000);
    }

    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  useEffect(() => {
    const inputElement = promptInputRef.current;

    if (inputElement) {
      inputElement.style.height = '0px';
      const { scrollHeight } = inputElement;

      inputElement.style.height = `${
        Math.min(scrollHeight, MAX_HEIGHT_PX) / PIXELS_PER_REM
      }rem`;
    }
  }, [value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // eslint-disable-next-line no-void
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (
      (promptInputTextSize === 0 && !uploadedFile) ||
      isLoading ||
      isCharacterLimitExceeded
    ) {
      return;
    }

    dispatch(setPromptFooterState({ isLoading: true }));

    // Store values before clearing
    const currentValue = value;
    const currentFile = uploadedFile;

    dispatch(resetPromptFooterState());
    setUploadProgress(null);

    if (mode === 'upload') {
      dispatch(setPromptFooterMode('text'));
    }

    if (onFileSubmit && (currentFile || currentValue.trim())) {
      onFileSubmit(currentFile, currentValue);
    }

    try {
      // let base64Content = '';
      // if (currentFile) {
      //   base64Content = await convertFileToBase64(currentFile);
      // }
      // const response = await convertFileToPrompt({
      //   fileContent: base64Content,
      //   userPrompt: currentValue,
      //   contentType: currentFile?.type ?? 'application/json',
      //   fileName: currentFile?.name ?? '',
      //   useCase: 'createAccount',
      // }).unwrap();
      // Logger.log('API Response:', response);
    } catch (error) {
      Logger.error('Submit API failed:', error);
    } finally {
      dispatch(setPromptFooterState({ isLoading: false }));
    }
  };

  return (
    <div
      className={`PromptFooter ${
        isFullscreen ? 'PromptFooter--fullscreen' : ''
      }`}>
      <div className='PromptFooter-Container'>
        <div
          className={`PromptFooter-ChatInput ${
            isDisabled ? 'PromptFooter-ChatInput--disabled' : ''
          } ${
            isCharacterLimitExceeded || fileSizeError
              ? 'PromptFooter-ChatInput--error'
              : ''
          }`}>
          {uploadedFile && (
            <div className='PromptFooter-FilePromptContainer'>
              <FilePrompt
                file={uploadedFile}
                onRemove={() => {
                  dispatch(
                    setPromptFooterState({
                      uploadedFile: null,
                      fileSizeError: null,
                    })
                  );
                  setUploadProgress(null);

                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                isCloseVisible
                isProgressVisible
                uploadProgress={uploadProgress}
              />
            </div>
          )}
          <div className='PromptFooter-InputRow'>
            <div className='PromptFooter-InputField-prefixIcon'>
              <ButtonIcon
                kind='saviGhost'
                size='medium'
                icon={
                  <Icon
                    kind='Add'
                    size='small'
                    color={
                      isDisabled
                        ? '--color-foreground-neutral-disabled'
                        : '--color-foreground-neutral-bold'
                    }
                  />
                }
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                dataTestId='prompt-prefix-icon'
                isDisabled={isDisabled || !!uploadedFile || mode === 'text'}
              />
            </div>
            <div className='PromptFooter-InputfieldMain'>
              <InputField
                className={`PromptFooter-Inputfield ${
                  isCharacterLimitExceeded
                    ? 'PromptFooter-Inputfield--error'
                    : ''
                } ${uploadedFile ? 'PromptFooter-Inputfield--hidden' : ''}`}
                kind='multiline'
                name='prompt-input'
                placeholder={
                  uploadedFile
                    ? ''
                    : 'How can I help you set up the Integration Agent?'
                }
                isResetButtonVisible={false}
                value={value}
                setValue={(newValue: string) => {
                  dispatch(setPromptFooterState({ value: newValue }));
                }}
                onKeyDown={handleKeyDown}
                isDisabled={
                  isDisabled ||
                  (mode === 'upload' &&
                    (!!uploadedFile || uploadProgress !== null))
                }
                ref={promptInputRef}
                dataTestId='prompt-input-field'
                shouldDisallowBrackets={false}
              />
            </div>

            <div
              className='PromptFooter-InputField-suffixButton'
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
              }}>
              {characterCount > 200 && (
                <Typography
                  kind='body3'
                  color={
                    isCharacterLimitExceeded
                      ? '--color-foreground-danger'
                      : '--color-foreground-neutral-subtle'
                  }>
                  {characterCount}/{maxCharacters}
                </Typography>
              )}
              <ButtonIcon
                size='medium'
                kind='saviGhost'
                isDisabled={
                  (promptInputTextSize === 0 && !uploadedFile) ||
                  isCharacterLimitExceeded ||
                  (uploadProgress !== null && uploadProgress < 100) ||
                  !!fileSizeError
                }
                icon={
                  <Icon
                    kind={isLoading ? 'StopFilled' : 'ArrowUp'}
                    className={
                      isDisabled
                        ? 'PromptFooter-suffixButtonIcon--isDisabled'
                        : ''
                    }
                    size='small'
                  />
                }
                onClick={handleSubmit}
                dataTestId='prompt-suffix-button'
              />
            </div>
          </div>
        </div>
        {isCharacterLimitExceeded && (
          <div className='PromptFooter-ErrorContainer'>
            <Icon
              kind='AlertCriticalFilled'
              size='small'
              className='PromptFooter-ErrorIcon'
              color='--color-foreground-critical-medium'
            />
            <Typography
              kind='body3'
              color='--color-foreground-critical-medium'
              className='PromptFooter-Error'>
              Character limit exceeded 2000 letters. Please shorten your input.
            </Typography>
          </div>
        )}
        {fileSizeError && (
          <div className='PromptFooter-ErrorContainer'>
            <Icon
              kind='AlertCriticalFilled'
              size='small'
              className='PromptFooter-ErrorIcon'
              color='--color-foreground-critical-medium'
            />
            <Typography
              kind='body3'
              color='--color-foreground-critical-medium'
              className='PromptFooter-Error'>
              File size exceeds 5MB limit
            </Typography>
          </div>
        )}
        <Typography
          kind='body3'
          color='--color-foreground-neutral-subtle'
          className='PromptFooter-Hint'>
          Savi is always learning new things. Results may vary.
        </Typography>
        <input
          type='file'
          ref={fileInputRef}
          onChange={handleFileChange}
          accept='.json,application/json'
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
}

export default PromptFooter;
